import { useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import renderMathInElement from 'katex/contrib/auto-render';
import 'katex/dist/katex.min.css';
import { markdownToHtml } from '../../../lib/markdownLite';

/** A coach answer: Markdown (headings, lists, tables, code) with KaTeX maths. */
export default function CoachMarkdown({ text, streaming = false }: { text: string; streaming?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const html = useMemo(() => DOMPurify.sanitize(markdownToHtml(text), { ADD_ATTR: ['target'] }), [text]);
  useEffect(() => {
    if (!ref.current || streaming) return;
    try {
      renderMathInElement(ref.current, {
        delimiters: [{ left: '$$', right: '$$', display: true }, { left: '$', right: '$', display: false }],
        throwOnError: false,
      });
    } catch { /* leave the raw maths */ }
  }, [html, streaming]);
  return (
    <div ref={ref} data-testid="coach-markdown"
      className="coach-md min-w-0 break-words text-sm leading-relaxed text-ink [&_a]:font-semibold [&_a]:text-slate [&_a]:underline [&_code]:rounded [&_code]:bg-surface-2 [&_code]:px-1 [&_code]:font-mono [&_code]:text-[12px] [&_h3]:mb-1 [&_h3]:mt-3 [&_h3]:font-display [&_h3]:text-[15px] [&_h3]:font-bold [&_h4]:mb-1 [&_h4]:mt-3 [&_h4]:font-display [&_h4]:text-sm [&_h4]:font-bold [&_h5]:mt-2 [&_h5]:font-semibold [&_li]:my-0.5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1.5 [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-[10px] [&_pre]:bg-surface-2 [&_pre]:p-3 [&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[13px] [&_td]:border-b [&_td]:border-hairline [&_td]:px-2 [&_td]:py-1.5 [&_th]:border-b [&_th]:border-line [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
      dangerouslySetInnerHTML={{ __html: html }} />
  );
}
